import type { MathSkill, MathTier } from "../math";
import type { CharacterId } from "./core/types";
import type { PlatformDefinition } from "./platforming";

export const LEVEL_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export type LevelNumber = (typeof LEVEL_NUMBERS)[number];
export const TOTAL_LEVELS = LEVEL_NUMBERS.length;
export const LEVEL_WORLD_WIDTH = 3260;

/**
 * A stage floor keeps the campaign challenging even when the saved adaptive
 * mastery is still low. Two consecutive mistakes still activate the gentler
 * support tier, so this ramp raises the baseline without removing assistance.
 */
export const LEVEL_MATH_TIER_FLOORS = {
  1: 2,
  2: 2,
  3: 3,
  4: 3,
  5: 4,
  6: 5,
  7: 5,
  8: 6,
  9: 6,
  10: 6,
} as const satisfies Readonly<Record<LevelNumber, MathTier>>;

export type EnemyBehavior = "contact" | "ranged";
export type LevelCollectibleKind = "coin" | "gem" | "heart";

/** Display sizes shared with the Phaser scene so placement math and rendering agree. */
export const ENEMY_DISPLAY_SIZES = {
  contact: 94,
  ranged: 104,
} as const satisfies Readonly<Record<EnemyBehavior, number>>;

export const COLLECTIBLE_DISPLAY_SIZES = {
  coin: 46,
  gem: 54,
  heart: 50,
} as const satisfies Readonly<Record<LevelCollectibleKind, number>>;

/** Minimum air gap between a floating collectible and a patrolling enemy's head. */
export const ENEMY_CLEARANCE_GAP = 12;

export interface LevelPosition {
  readonly x: number;
  readonly y: number;
}

export interface QuestionBoxSlot extends LevelPosition {
  readonly id: string;
  /** Zero-based index into this level's platform layout. */
  readonly platformIndex: number;
}

export interface CollectibleSlot extends LevelPosition {
  readonly id: string;
  readonly kind: LevelCollectibleKind;
}

/**
 * Theme-neutral enemy behavior paired with a character-specific sprite by the
 * Phaser scene.
 */
export interface EnemySlot extends LevelPosition {
  readonly id: string;
  readonly behavior: EnemyBehavior;
  readonly patrolMinX: number;
  readonly patrolMaxX: number;
  readonly size: number;
}

export interface CheckpointSlot extends LevelPosition {
  readonly id: string;
  readonly platformIndex: number;
}

export interface GoalSlot extends LevelPosition {
  readonly platformIndex: number;
}

export interface LevelDefinition {
  readonly character: CharacterId;
  readonly number: LevelNumber;
  readonly id: string;
  readonly displayName: string;
  readonly platforms: readonly PlatformDefinition[];
  readonly questionBoxes: readonly QuestionBoxSlot[];
  readonly collectibles: readonly CollectibleSlot[];
  readonly enemies: readonly EnemySlot[];
  readonly checkpoints: readonly CheckpointSlot[];
  readonly goal: GoalSlot;
  readonly questionSkillRotation: readonly MathSkill[];
  readonly mathTierFloor: MathTier;
}

type RouteSegment = readonly [
  width: number,
  top: number,
  gapAfter: number,
];

type EnemyBlueprint = readonly [
  platformIndex: number,
  behavior: EnemyBehavior,
  horizontalRatio?: number,
];

type CheckpointBlueprint = readonly [
  platformIndex: number,
  horizontalRatio: number,
];

type CollectibleStyle = "trail" | "arc" | "peaks";

interface LevelBlueprint {
  readonly number: LevelNumber;
  readonly displayName: string;
  readonly mathTierFloor: MathTier;
  readonly route: readonly RouteSegment[];
  readonly questionPlatformIndices: readonly [number, number, number];
  readonly enemies: readonly EnemyBlueprint[];
  readonly checkpoints: readonly [CheckpointBlueprint, CheckpointBlueprint];
  readonly heartPlatformIndices: readonly number[];
  readonly collectibleStyle: CollectibleStyle;
}

export const LEVEL_QUESTION_SKILL_ROTATIONS = {
  dragon: {
    1: ["counting", "sequence", "addition"],
    2: ["counting", "addition", "compare"],
    3: ["addition", "subtraction", "sequence"],
    4: ["subtraction", "missing-number", "word-problems"],
    5: ["tens", "addition", "compare"],
    6: ["sequence", "missing-number", "subtraction"],
    7: ["word-problems", "missing-number", "tens"],
    8: ["subtraction", "compare", "tens"],
    9: ["sequence", "word-problems", "tens"],
    10: ["missing-number", "subtraction", "tens"],
  },
  unicorn: {
    1: ["counting", "addition", "sequence"],
    2: ["sequence", "compare", "counting"],
    3: ["subtraction", "addition", "compare"],
    4: ["missing-number", "word-problems", "addition"],
    5: ["tens", "missing-number", "compare"],
    6: ["subtraction", "missing-number", "sequence"],
    7: ["word-problems", "addition", "tens"],
    8: ["missing-number", "subtraction", "tens"],
    9: ["sequence", "tens", "word-problems"],
    10: ["compare", "missing-number", "tens"],
  },
} as const satisfies Readonly<
  Record<CharacterId, Readonly<Record<LevelNumber, readonly MathSkill[]>>>
>;

const DRAGON_BLUEPRINTS = [
  {
    number: 1,
    displayName: "שביל הגחלים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[1],
    route: [[560, 610, 40], [260, 505, 70], [380, 610, 50], [260, 470, 75], [340, 575, 55], [280, 500, 65], [620, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", 0.2], [4, "ranged", -0.18]],
    checkpoints: [[2, -0.28], [4, 0.28]],
    heartPlatformIndices: [4],
    collectibleStyle: "trail",
  },
  {
    number: 2,
    displayName: "גשרי הלבה",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[2],
    route: [[500, 610, 45], [240, 540, 55], [290, 455, 60], [320, 590, 45], [250, 480, 70], [280, 555, 50], [270, 445, 55], [600, 610, 0]],
    questionPlatformIndices: [1, 4, 6],
    enemies: [[2, "contact", -0.15], [5, "ranged", 0.12], [7, "contact", -0.25]],
    checkpoints: [[3, -0.3], [5, -0.3]],
    heartPlatformIndices: [3],
    collectibleStyle: "arc",
  },
  {
    number: 3,
    displayName: "מערת האודם",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[3],
    route: [[540, 610, 50], [300, 490, 40], [360, 600, 55], [280, 460, 45], [370, 565, 60], [300, 430, 50], [600, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.22], [2, "ranged", 0.24], [4, "contact", 0.18], [6, "ranged", -0.24]],
    checkpoints: [[2, 0], [4, -0.28]],
    heartPlatformIndices: [2, 4],
    collectibleStyle: "peaks",
  },
  {
    number: 4,
    displayName: "מגדלי העשן",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[4],
    route: [[460, 610, 40], [210, 520, 55], [230, 440, 45], [250, 555, 60], [220, 475, 50], [250, 600, 45], [230, 490, 55], [230, 420, 50], [500, 610, 0]],
    questionPlatformIndices: [2, 4, 7],
    enemies: [[3, "contact", -0.2], [5, "ranged", 0.18], [6, "contact", -0.16], [8, "ranged", -0.25]],
    checkpoints: [[3, 0.28], [6, 0.28]],
    heartPlatformIndices: [3, 6],
    collectibleStyle: "arc",
  },
  {
    number: 5,
    displayName: "נהר האש",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[5],
    route: [[480, 610, 60], [260, 470, 45], [300, 545, 70], [270, 410, 50], [320, 535, 65], [260, 455, 40], [300, 580, 55], [560, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.18], [4, "ranged", 0.2], [6, "contact", -0.2], [7, "ranged", -0.24]],
    checkpoints: [[2, 0.28], [6, 0.28]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 6,
    displayName: "מצודת האבירים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[6],
    route: [[550, 610, 50], [300, 520, 80], [340, 390, 40], [280, 530, 70], [360, 445, 45], [300, 565, 65], [620, 610, 0]],
    questionPlatformIndices: [1, 2, 4],
    enemies: [[3, "contact", -0.2], [3, "ranged", 0.22], [5, "contact", -0.22], [6, "ranged", -0.25], [6, "contact", 0.18]],
    checkpoints: [[3, 0], [5, 0.28]],
    heartPlatformIndices: [3, 5],
    collectibleStyle: "trail",
  },
  {
    number: 7,
    displayName: "לוע הר הגעש",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[7],
    route: [[500, 610, 45], [250, 455, 65], [280, 570, 55], [310, 430, 50], [250, 550, 75], [300, 405, 45], [280, 520, 60], [600, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.2], [4, "ranged", 0.18], [6, "contact", -0.2], [7, "ranged", -0.28], [7, "contact", 0.2]],
    checkpoints: [[2, 0.26], [6, 0.26]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 8,
    displayName: "מערת הדרקונים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[8],
    route: [[470, 610, 55], [220, 500, 45], [250, 420, 60], [230, 540, 50], [270, 460, 65], [240, 575, 45], [260, 435, 55], [230, 520, 50], [520, 610, 0]],
    questionPlatformIndices: [1, 4, 6],
    enemies: [[2, "ranged", 0], [3, "contact", -0.18], [5, "contact", 0.18], [7, "ranged", -0.16], [8, "contact", -0.26]],
    checkpoints: [[3, 0.26], [7, 0.26]],
    heartPlatformIndices: [3, 7],
    collectibleStyle: "arc",
  },
  {
    number: 9,
    displayName: "כתר הלהבות",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[9],
    route: [[500, 610, 55], [270, 460, 60], [300, 565, 45], [260, 415, 75], [300, 530, 50], [280, 400, 65], [310, 515, 55], [580, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.2], [4, "ranged", 0.2], [6, "contact", -0.2], [7, "ranged", -0.28], [7, "contact", 0], [7, "contact", 0.28]],
    checkpoints: [[2, 0.26], [6, 0.26]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 10,
    displayName: "פסגת דרקון האור",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[10],
    route: [[480, 610, 45], [230, 455, 55], [250, 555, 50], [240, 410, 65], [260, 525, 45], [250, 390, 60], [270, 505, 50], [240, 430, 55], [560, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.2], [4, "ranged", 0.18], [6, "contact", -0.18], [7, "ranged", 0.12], [8, "contact", -0.28], [8, "ranged", 0.25]],
    checkpoints: [[2, 0.26], [6, 0.26]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "arc",
  },
] as const satisfies readonly LevelBlueprint[];

const UNICORN_BLUEPRINTS = [
  {
    number: 1,
    displayName: "שביל הלבבות",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[1],
    route: [[580, 610, 45], [250, 525, 60], [360, 440, 55], [280, 565, 50], [350, 475, 70], [260, 540, 50], [600, 610, 0]],
    questionPlatformIndices: [1, 2, 4],
    enemies: [[3, "contact", -0.18], [5, "ranged", 0.16]],
    checkpoints: [[3, 0.25], [5, -0.25]],
    heartPlatformIndices: [3],
    collectibleStyle: "arc",
  },
  {
    number: 2,
    displayName: "גשרי הקשת",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[2],
    route: [[500, 610, 50], [260, 480, 45], [280, 550, 70], [300, 425, 55], [250, 535, 60], [290, 455, 45], [270, 575, 65], [620, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.18], [4, "ranged", 0.2], [7, "contact", -0.25]],
    checkpoints: [[2, 0.25], [6, 0.25]],
    heartPlatformIndices: [2],
    collectibleStyle: "trail",
  },
  {
    number: 3,
    displayName: "גן הכוכבים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[3],
    route: [[460, 610, 45], [220, 540, 55], [250, 460, 45], [240, 390, 65], [260, 500, 50], [230, 430, 60], [250, 560, 45], [240, 470, 55], [540, 610, 0]],
    questionPlatformIndices: [2, 4, 7],
    enemies: [[1, "contact", 0], [3, "ranged", 0], [5, "contact", 0], [8, "ranged", -0.24]],
    checkpoints: [[4, -0.25], [6, 0.25]],
    heartPlatformIndices: [4, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 4,
    displayName: "מגדלי העננים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[4],
    route: [[540, 610, 60], [300, 465, 50], [330, 575, 75], [290, 435, 45], [360, 540, 65], [280, 410, 55], [650, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.2], [4, "ranged", 0.2], [6, "contact", -0.28], [6, "ranged", 0.25]],
    checkpoints: [[2, 0.25], [4, -0.25]],
    heartPlatformIndices: [2, 4],
    collectibleStyle: "arc",
  },
  {
    number: 5,
    displayName: "אגם המשאלות",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[5],
    route: [[480, 610, 55], [250, 500, 70], [310, 405, 45], [270, 535, 60], [300, 450, 50], [260, 570, 75], [300, 440, 55], [590, 610, 0]],
    questionPlatformIndices: [1, 2, 4],
    enemies: [[3, "contact", -0.18], [5, "ranged", 0.18], [6, "contact", -0.18], [7, "ranged", -0.24]],
    checkpoints: [[3, 0.25], [6, 0.25]],
    heartPlatformIndices: [3, 6],
    collectibleStyle: "trail",
  },
  {
    number: 6,
    displayName: "יער השדונים",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[6],
    route: [[470, 610, 50], [230, 455, 45], [240, 550, 60], [260, 420, 55], [230, 520, 45], [260, 395, 70], [240, 515, 50], [250, 445, 60], [550, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", 0], [4, "ranged", 0], [6, "contact", 0], [7, "ranged", 0], [8, "contact", -0.25]],
    checkpoints: [[2, 0.25], [6, 0.25]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 7,
    displayName: "עמק הרוחות",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[7],
    route: [[560, 610, 55], [280, 490, 65], [350, 380, 45], [300, 520, 70], [340, 410, 50], [300, 545, 60], [620, 610, 0]],
    questionPlatformIndices: [1, 2, 4],
    enemies: [[3, "contact", -0.2], [3, "ranged", 0.22], [5, "contact", -0.2], [6, "ranged", -0.26], [6, "contact", 0.2]],
    checkpoints: [[3, 0], [5, 0.25]],
    heartPlatformIndices: [3, 5],
    collectibleStyle: "arc",
  },
  {
    number: 8,
    displayName: "ארמון הקשת",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[8],
    route: [[510, 610, 45], [240, 530, 75], [300, 400, 50], [270, 545, 65], [290, 420, 45], [270, 560, 70], [300, 435, 55], [600, 610, 0]],
    questionPlatformIndices: [1, 2, 4],
    enemies: [[3, "contact", -0.2], [5, "ranged", 0.2], [6, "contact", -0.18], [7, "ranged", -0.28], [7, "contact", 0.2]],
    checkpoints: [[3, 0.25], [6, 0.25]],
    heartPlatformIndices: [3, 6],
    collectibleStyle: "peaks",
  },
  {
    number: 9,
    displayName: "שערי אור הירח",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[9],
    route: [[460, 610, 55], [220, 470, 45], [260, 555, 65], [240, 405, 50], [250, 525, 70], [250, 390, 45], [260, 510, 60], [240, 430, 50], [560, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", 0], [4, "ranged", 0], [6, "contact", 0], [7, "ranged", 0], [8, "contact", -0.28], [8, "ranged", 0.26]],
    checkpoints: [[2, 0.25], [6, 0.25]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "trail",
  },
  {
    number: 10,
    displayName: "פסגת כתר הקסם",
    mathTierFloor: LEVEL_MATH_TIER_FLOORS[10],
    route: [[500, 610, 50], [260, 455, 65], [290, 565, 45], [280, 410, 75], [310, 540, 55], [270, 395, 65], [300, 520, 50], [620, 610, 0]],
    questionPlatformIndices: [1, 3, 5],
    enemies: [[2, "contact", -0.2], [4, "ranged", 0.2], [6, "contact", -0.18], [7, "ranged", -0.28], [7, "contact", 0], [7, "ranged", 0.27]],
    checkpoints: [[2, 0.25], [6, 0.25]],
    heartPlatformIndices: [2, 6],
    collectibleStyle: "arc",
  },
] as const satisfies readonly LevelBlueprint[];

function createPlatforms(route: readonly RouteSegment[], seed: number): readonly PlatformDefinition[] {
  let left = 0;
  return route.map(([width, top, gapAfter], index) => {
    const raisedHeights = [72, 80, 88] as const;
    const height = top >= 590 ? 110 : raisedHeights[(index + seed) % raisedHeights.length];
    const platform = [left + width / 2, top + height / 2, width, height] as const;
    left += width + gapAfter;
    return platform;
  });
}

function pointOnPlatform(
  platforms: readonly PlatformDefinition[],
  platformIndex: number,
  horizontalRatio: number,
  lift: number,
): LevelPosition {
  const [x, y, width, height] = platforms[platformIndex];
  return {
    x: Math.round(x + width * horizontalRatio),
    y: Math.round(y - height / 2 - lift),
  };
}

function createQuestionBoxes(
  character: CharacterId,
  blueprint: LevelBlueprint,
  platforms: readonly PlatformDefinition[],
): readonly QuestionBoxSlot[] {
  const baseRatios = character === "dragon" ? [-0.12, 0.1, -0.08] : [0.12, -0.1, 0.08];
  return blueprint.questionPlatformIndices.map((platformIndex, questionIndex) => ({
    id: `${character}-level-${blueprint.number}-box-${questionIndex + 1}`,
    platformIndex,
    ...pointOnPlatform(platforms, platformIndex, baseRatios[questionIndex], 44),
  }));
}

function coinPlacement(style: CollectibleStyle, index: number): { readonly ratio: number; readonly lift: number } {
  if (style === "arc") {
    return { ratio: [-0.2, 0, 0.2][index % 3], lift: [52, 72, 52][index % 3] };
  }
  if (style === "peaks") {
    return { ratio: index % 2 === 0 ? -0.18 : 0.18, lift: index % 2 === 0 ? 58 : 88 };
  }
  return { ratio: [-0.22, 0.22, 0][index % 3], lift: 52 };
}

function createCollectibles(
  character: CharacterId,
  blueprint: LevelBlueprint,
  platforms: readonly PlatformDefinition[],
): readonly CollectibleSlot[] {
  const prefix = `${character}-level-${blueprint.number}`;

  const tallestEnemyByPlatform = new Map<number, number>();
  for (const [platformIndex, behavior] of blueprint.enemies) {
    const size = ENEMY_DISPLAY_SIZES[behavior];
    const tallest = tallestEnemyByPlatform.get(platformIndex) ?? 0;
    tallestEnemyByPlatform.set(platformIndex, Math.max(tallest, size));
  }

  // Enemies patrol nearly the full platform, so a floating item must hover
  // above their heads or the sprites visibly intersect as the enemy walks by.
  const clearOfEnemies = (
    platformIndex: number,
    kind: LevelCollectibleKind,
    lift: number,
  ): number => {
    const enemySize = tallestEnemyByPlatform.get(platformIndex);
    if (enemySize === undefined) return lift;
    return Math.max(lift, enemySize + COLLECTIBLE_DISPLAY_SIZES[kind] / 2 + ENEMY_CLEARANCE_GAP);
  };

  const collectibles: CollectibleSlot[] = platforms.map((_platform, platformIndex) => {
    const placement = platformIndex === 0
      ? { ratio: 0.34, lift: 52 }
      : coinPlacement(blueprint.collectibleStyle, platformIndex);
    return {
      id: `${prefix}-coin-${platformIndex + 1}`,
      kind: "coin",
      ...pointOnPlatform(
        platforms,
        platformIndex,
        placement.ratio,
        clearOfEnemies(platformIndex, "coin", placement.lift),
      ),
    };
  });

  const bonusCoinCount = blueprint.number % 3;
  const finalPlatformIndex = platforms.length - 1;
  for (let index = 0; index < bonusCoinCount; index += 1) {
    collectibles.push({
      id: `${prefix}-coin-bonus-${index + 1}`,
      kind: "coin",
      ...pointOnPlatform(
        platforms,
        finalPlatformIndex,
        index === 0 ? -0.25 : 0.25,
        clearOfEnemies(finalPlatformIndex, "coin", 58 + index * 18),
      ),
    });
  }

  blueprint.questionPlatformIndices.forEach((platformIndex, index) => {
    collectibles.push({
      id: `${prefix}-gem-${index + 1}`,
      kind: "gem",
      ...pointOnPlatform(
        platforms,
        platformIndex,
        index % 2 === 0 ? 0.27 : -0.27,
        clearOfEnemies(platformIndex, "gem", 104),
      ),
    });
  });

  blueprint.heartPlatformIndices.forEach((platformIndex, index) => {
    collectibles.push({
      id: `${prefix}-heart-${index + 1}`,
      kind: "heart",
      ...pointOnPlatform(
        platforms,
        platformIndex,
        index % 2 === 0 ? 0.32 : -0.32,
        clearOfEnemies(platformIndex, "heart", 54),
      ),
    });
  });

  return collectibles;
}

function createEnemies(
  character: CharacterId,
  blueprint: LevelBlueprint,
  platforms: readonly PlatformDefinition[],
): readonly EnemySlot[] {
  return blueprint.enemies.map(([platformIndex, behavior, horizontalRatio = 0], index) => {
    const [platformX, platformY, platformWidth, platformHeight] = platforms[platformIndex];
    const size = ENEMY_DISPLAY_SIZES[behavior];
    const patrolInset = size / 2 + 14;
    const patrolMinX = Math.round(platformX - platformWidth / 2 + patrolInset);
    const patrolMaxX = Math.round(platformX + platformWidth / 2 - patrolInset);
    const requestedX = Math.round(platformX + platformWidth * horizontalRatio);
    return {
      id: `${character}-level-${blueprint.number}-enemy-${index + 1}`,
      behavior,
      x: Math.min(patrolMaxX, Math.max(patrolMinX, requestedX)),
      y: Math.round(platformY - platformHeight / 2 - size / 2),
      patrolMinX,
      patrolMaxX,
      size,
    };
  });
}

function createCheckpoints(
  character: CharacterId,
  blueprint: LevelBlueprint,
  platforms: readonly PlatformDefinition[],
): readonly CheckpointSlot[] {
  return blueprint.checkpoints.map(([platformIndex, horizontalRatio], index) => ({
    id: `${character}-level-${blueprint.number}-checkpoint-${index + 1}`,
    platformIndex,
    ...pointOnPlatform(platforms, platformIndex, horizontalRatio, 46),
  }));
}

function createLevelDefinition(
  character: CharacterId,
  blueprint: LevelBlueprint,
): LevelDefinition {
  const characterSeed = character === "dragon" ? 0 : 1;
  const platforms = createPlatforms(blueprint.route, blueprint.number + characterSeed);
  const goalPlatformIndex = platforms.length - 1;
  return {
    character,
    number: blueprint.number,
    id: `${character}-level-${blueprint.number}`,
    displayName: blueprint.displayName,
    platforms,
    questionBoxes: createQuestionBoxes(character, blueprint, platforms),
    collectibles: createCollectibles(character, blueprint, platforms),
    enemies: createEnemies(character, blueprint, platforms),
    checkpoints: createCheckpoints(character, blueprint, platforms),
    goal: {
      platformIndex: goalPlatformIndex,
      ...pointOnPlatform(platforms, goalPlatformIndex, 0.25, 78),
    },
    questionSkillRotation: LEVEL_QUESTION_SKILL_ROTATIONS[character][blueprint.number],
    mathTierFloor: blueprint.mathTierFloor,
  };
}

function createCharacterLevels(
  character: CharacterId,
  blueprints: readonly LevelBlueprint[],
): Readonly<Record<LevelNumber, LevelDefinition>> {
  return Object.fromEntries(
    blueprints.map((blueprint) => [blueprint.number, createLevelDefinition(character, blueprint)]),
  ) as Record<LevelNumber, LevelDefinition>;
}

const LEVEL_DEFINITIONS: Readonly<
  Record<CharacterId, Readonly<Record<LevelNumber, LevelDefinition>>>
> = {
  dragon: createCharacterLevels("dragon", DRAGON_BLUEPRINTS),
  unicorn: createCharacterLevels("unicorn", UNICORN_BLUEPRINTS),
};

export function clampLevelNumber(value: number): LevelNumber {
  if (!Number.isFinite(value) || value <= 1) return 1;
  if (value >= TOTAL_LEVELS) return TOTAL_LEVELS;
  return Math.round(value) as LevelNumber;
}

export function getLevelDefinition(
  character: CharacterId,
  levelNumber: number,
): LevelDefinition {
  return LEVEL_DEFINITIONS[character][clampLevelNumber(levelNumber)];
}
